import { useDispatch, useSelector } from "react-redux";
import { waterCategories, airCategories, landCategories, snowCategories, bestViewsCategories } from "../../data/categoryList.js";
import { setNewFilters } from "../../redux/filtersSlice.ts";
import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

const flatArray = [
    ...waterCategories,
    ...airCategories,
    ...landCategories,
    ...snowCategories,
    ...bestViewsCategories
]

const CategorySlidebar = ({ isSmall }) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();
    const filters = useSelector(state => state.filters);

    const [isAllLeft, setIsAllLeft] = useState(false);
    const [isAllRight, setIsAllRight] = useState(true);


    const handleClick = (label) => {
        if (label === filters.category) {
            dispatch(setNewFilters({ category: '' }))
        } else {
            dispatch(setNewFilters({ category: label }))
        }
    }

    // Gestisco il click per lo scorrimento
    const scrollContainerRef = useRef(null);
    const handleHorizontalScroll = (direction) => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: direction === 'right' ? 400 : -400, behavior: 'smooth' })
        }
    }

    // Gestisco il scroll orizzontale
    const handleScroll = useCallback(() => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            /* Imposto allLeftShow se lo scroll è tutto a sinistra */
            setIsAllLeft(scrollLeft === 0);
            /* Imposto allRightShow se lo scroll è tutto a destra */
            setIsAllRight(scrollLeft + clientWidth >= scrollWidth - 1);
        }
    }, []);

    // Funzione per verificare se il contenuto necessita di scroll orizzontale
    const checkScrollNeeded = useCallback(() => {
        if (scrollContainerRef.current) {
            const { scrollWidth, clientWidth } = scrollContainerRef.current;
            const isScrollNeeded = scrollWidth > clientWidth;
            
            if (!isScrollNeeded) {
                // Se non è necessario lo scroll, entrambi devono essere true
                setIsAllLeft(true);
                setIsAllRight(true);
            } else {
                // Se è necessario lo scroll, usa la logica normale
                handleScroll();
            }
        }
    }, [handleScroll]);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
            // Controlla immediatamente se è necessario lo scroll
            checkScrollNeeded();
        }
        // Cleanup
        return () => {
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', handleScroll);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleScroll]);

    // Controlla se è necessario lo scroll quando cambiano le categorie o la finestra viene ridimensionata
    useEffect(() => {
        checkScrollNeeded();
    }, [filters.activityType, checkScrollNeeded]);

    // Controlla se è necessario lo scroll al ridimensionamento della finestra
    useEffect(() => {
        const handleResize = () => {
            checkScrollNeeded();
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [checkScrollNeeded]);

    // Gestisco lo scroll automatico orizzontale alla categoria selezionata
    useEffect(() => {
        if (filters.category && scrollContainerRef.current) {
            const selectedCategoryElement = scrollContainerRef.current.querySelector(`[data-category="${filters.category}"]`);
            if (selectedCategoryElement) {
                const containerWidth = scrollContainerRef.current.clientWidth;
                const elementOffsetLeft = selectedCategoryElement.offsetLeft;
                const elementWidth = selectedCategoryElement.offsetWidth;
                // Calcola la posizione di scroll per centrare l'elemento
                const scrollPosition = elementOffsetLeft - (containerWidth / 2) + (elementWidth / 2);
                scrollContainerRef.current.scrollTo({
                    left: Math.max(0, scrollPosition), // assicura che non vada sotto 0
                })
            }
        }
    }, [filters.category, filters.activityType])

    return (
        <div className={`
                flex gap-2 items-center justify-center
                w-full lg:w-[800px] xl:w-[1200px] relative
            `}>
            <div className={`
                flex
                ${isSmall ? 'md:gap-8 min-h-[52px]' : 'gap-6 md:gap-16'}
                overflow-x-auto whitespace-nowrap hide-scrollbar
                flex items-center
                py-1
                `}
                style={{ WebkitOverflowScrolling: 'touch' }}
                ref={scrollContainerRef}
            >
                {
                    filters.activityType?.includes("Acqua") &&
                    waterCategories.map((element, index) => {
                        return <div
                            key={index}
                            data-category={element.name}
                            className={`
                            flex flex-col gap-1 items-center p-2 text-[8pt] w-[72px] text-center cursor-pointer border-2 rounded-xl shrink-0
                            ${isSmall ? '!p-1' : 'p-2'}
                            ${filters.category === element.name ?
                                    'border-neutral-500' :
                                    'border-transparent'}
                                ${index === 0 && !isSmall && 'ml-8'}
                                ${element.name === 'Windsurf' && 'mr-16'}
                            `}>
                            <img src={element.icon} alt={`${t(element.label)} icon`} className={`min-w-12 h-12 cursor-pointer ${isSmall ? 'min-w-8 h-8' : ''}`} onClick={() => handleClick(element.name)} />
                            <div className={`${isSmall ? 'text-neutral-400' : ''}`}>{t(element.shortLabel)}</div>
                        </div>
                    })
                }
                {
                    filters.activityType?.includes("Aria") &&
                    airCategories.map((element, index) => {
                        return <div
                            key={index}
                            data-category={element.name}
                            className={`
                            flex flex-col gap-1 items-center p-2 text-[8pt] w-[72px] text-center cursor-pointer border-2 rounded-xl shrink-0
                            ${isSmall ? '!p-1' : 'p-2'}
                            ${filters.category === element.name ?
                                    'border-neutral-500' :
                                    'border-transparent'}
                            ${index === 0 && !isSmall && 'ml-8'}
                            ${element.name === 'Volo in mongolfiera' && 'mr-16'}
                            `}>
                            <img src={element.icon} alt={`${t(element.label)} icon`} className={`min-w-12 h-12 cursor-pointer ${isSmall ? 'min-w-8 h-8' : ''}`} onClick={() => handleClick(element.name)} />
                            <div className={`${isSmall ? 'text-neutral-400' : ''}`}>{t(element.shortLabel)}</div>
                        </div>
                    })
                }
                {
                    filters.activityType?.includes("Terra") &&
                    landCategories.map((element, index) => {
                        return <div
                            key={index}
                            data-category={element.name}
                            className={`
                            flex flex-col gap-1 items-center p-2 text-[8pt] w-[72px] text-center cursor-pointer border-2 rounded-xl shrink-0
                            ${isSmall ? '!p-1' : 'p-2'}
                            ${filters.category === element.name ?
                                    'border-neutral-500' :
                                    'border-transparent'}
                            ${index === 0 && !isSmall && 'ml-8'}
                            ${element.name === 'Zoo e parchi faunistici' && 'mr-16'}
                            `}>
                            <img src={element.icon} alt={`${t(element.label)} icon`} className={`min-w-12 h-12 cursor-pointer ${isSmall ? 'min-w-8 h-8' : ''}`} onClick={() => handleClick(element.name)} />
                            <div className={`${isSmall ? 'text-neutral-400' : ''}`}>{t(element.shortLabel)}</div>
                        </div>
                    })
                }
                {
                    filters.activityType?.includes("Neve") &&
                    snowCategories.map((element, index) => {
                        return <div
                            key={index}
                            data-category={element.name}
                            className={`
                            flex flex-col gap-1 items-center p-2 text-[8pt] w-[72px] text-center cursor-pointer border-2 rounded-xl shrink-0
                            ${filters.category === element.name ?
                                    'border-neutral-500' :
                                    'border-transparent'}
                            ${index === 0 && !isSmall && 'ml-8'}
                            ${element.name === 'Slittino, bob e Skitti' && 'mr-16'}
                            `}>
                            <img src={element.icon} alt={`${t(element.label)} icon`} className={`min-w-12 h-12 cursor-pointer ${isSmall ? 'min-w-8 h-8' : ''}`} onClick={() => handleClick(element.name)} />
                            <div className={`${isSmall ? 'text-neutral-400' : ''}`}>{t(element.shortLabel)}</div>
                        </div>
                    })
                }
                {
                    filters.activityType?.includes("Best Views") &&
                    bestViewsCategories.map((element, index) => {
                        return <div
                            key={index}
                            data-category={element.name}
                            className={`
                            flex flex-col gap-1 items-center p-2 text-[8pt] w-[72px] text-center cursor-pointer border-2 rounded-xl shrink-0
                            ${isSmall ? '!p-1' : 'p-2'}
                            ${filters.category === element.name ?
                                    'border-neutral-500' :
                                    'border-transparent'}
                            ${index === 0 && !isSmall && 'ml-8'}
                            ${element.name === 'Slittino, bob e Skitti' && 'mr-16'}
                            `}>
                            <img src={element.icon} alt={`${t(element.label)} icon`} className={`min-w-12 h-12 cursor-pointer ${isSmall ? 'min-w-8 h-8' : ''}`} onClick={() => handleClick(element.name)} />
                            <div className={`${isSmall ? 'text-neutral-400' : ''}`}>{t(element.shortLabel)}</div>
                        </div>
                    })
                }
                {
                    filters.activityType && filters.activityType.length === 0 &&
                    flatArray.map((element, index) => {
                        return <div key={index} className={`
                        flex flex-col gap-1 items-center p-2 text-[8pt] w-[72px] text-center cursor-pointer border-2 rounded-xl shrink-0
                        ${isSmall ? '!p-1' : 'p-2'}
                        ${filters.category === element.name ?
                                'border-neutral-500' :
                                'border-transparent'}
                        ${index === 0 && !isSmall && 'ml-8'}
                        `}>
                            <img src={element.icon} alt={`${element.label} icon`} className={`min-w-12 h-12 cursor-pointer ${isSmall ? 'min-w-8 h-8' : ''}`} onClick={() => handleClick(element.name)} />
                            <div className={`${isSmall ? 'text-neutral-400' : ''}`}>{t(element.shortLabel)}</div>
                        </div>
                    })
                }
            </div>
            {/* LEFT ARROW */}
            {!isAllLeft && (
                <div className={`
                    absolute left-0
                    hidden md:flex
                    justify-center items-center w-24 h-full bg-gradient-to-r from-neutral-100 to-transparent
                    `}>
                    <div
                    className="pt-2 pr-2.5 pl-2 pb-0.5 border rounded-full bg-white cursor-pointer w-fit h-fit"
                    onClick={() => handleHorizontalScroll('left')}><i className="fi fi-br-angle-left text-xl"></i></div>
                </div>
            )}
            {/* RIGHT ARROW */}
            {!isAllRight && (
                <div className={`
                    absolute right-0
                    hidden md:flex
                    justify-center items-center w-24 h-full bg-gradient-to-l from-neutral-100 to-transparent
                    `}>
                    <div
                    className="pt-2 pr-2 pl-2.5 pb-0.5 border rounded-full bg-white cursor-pointer w-fit h-fit"
                    onClick={() => handleHorizontalScroll('right')}><i className="fi fi-br-angle-right text-xl"></i></div>
                </div>
            )}

        </div >
    )
}

export default CategorySlidebar